import gql from 'graphql-tag'
import { fragmentUser } from './fragments'

export const LOGIN = gql`
	${fragmentUser}
	mutation tokenAuth($email: String!, $password: String!) {
		tokenAuth(email: $email, password: $password) {
			token
			user {
				...User
			}
		}
	}
`

export const TOKEN_VERIFY = gql`
	${fragmentUser}
	mutation verifyToken($token: String!) {
		verifyToken(token: $token) {
			payload
			user {
				...User
			}
		}
	}
`

/**
 * GraphQL mutation for User-Registration
 * Creates an inactive User
 */

export const REGISTRATION_REQUEST = gql`
	mutation registrationRequest(
		$firstName: String!
		$lastName: String!
		$email: String!
		$password: String!
		$confirmPassword: String!
		$captchaKey: String!
		$captchaValue: String!
	) {
		registrationRequest(
			firstName: $firstName
			lastName: $lastName
			email: $email
			password: $password
			confirmPassword: $confirmPassword
			captchaKey: $captchaKey
			captchaValue: $captchaValue
		) {
			success
		}
	}
`

export const SUBMIT_SECURITY_INCIDENT = gql`
	mutation submitSecurityIncident(
		$contactName: String!
		$email: String!
		$phone: String
		$otherContact: String
		$reportTypes: [String!]!
		$description: String!
		$discoveredAt: DateTime!
		$measuresTaken: String
		$affectedInstances: Int!
		$hpmVersion: String!
		$operatingEnvironment: String!
		$additionalInformation: String
		$attachments: [Upload!]
		$captchaKey: String
		$captchaValue: String
	) {
		submitSecurityIncident(
			contactName: $contactName
			email: $email
			phone: $phone
			otherContact: $otherContact
			reportTypes: $reportTypes
			description: $description
			discoveredAt: $discoveredAt
			measuresTaken: $measuresTaken
			affectedInstances: $affectedInstances
			hpmVersion: $hpmVersion
			operatingEnvironment: $operatingEnvironment
			additionalInformation: $additionalInformation
			attachments: $attachments
			captchaKey: $captchaKey
			captchaValue: $captchaValue
		) {
			success
		}
	}
`

/**
 * GraphQL mutation for changing a user's password.
 *
 * This mutation requires the current password and the new password (input twice for confirmation).
 * It will update the user's password if the provided current password is correct and the new passwords match and meet the required criteria.
 *
 * Variables:
 *  - $currentPassword: The user's current password. It is mandatory.
 *  - $newPassword: The new password that the user wants to set. It is mandatory.
 *  - $confirmPassword: Confirmation of the new password, to ensure accuracy. It is mandatory.
 *
 * Mutation operation: changePassword
 * Input parameters:
 *  - currentPassword: The current password of the user.
 *  - newPassword: The new password that the user wants to set.
 *  - confirmPassword: Confirmation of the new password.
 *
 * Usage: This mutation is typically used in a password change form where a user inputs their current password and new password twice for verification.
 */
export const CHANGE_PASSWORD = gql`
	mutation changePassword(
		$currentPassword: String!
		$newPassword: String!
		$confirmPassword: String!
	) {
		changePassword(
			currentPassword: $currentPassword
			newPassword: $newPassword
			confirmPassword: $confirmPassword
		)
	}
`

/**
 * GraphQL mutation for requesting a password reset.
 *
 * This mutation accepts an email address as an input parameter and
 * triggers a password reset request for the associated user account.
 *
 * @param {string} email - The email address of the user requesting a password reset.
 *
 * @returns {Object} Response object indicating the status of the password reset request.
 */
export const PASSWORD_RESET_REQUEST = gql`
	mutation passwordResetRequest($email: String!) {
		passwordResetRequest(email: $email)
	}
`

/**
 * GraphQL mutation for confirming password reset.
 *
 * The `passwordResetConfirm` mutation requires the following parameters:
 *
 * - newPassword: The new password to be set.
 * - confirmPassword: Confirmation of the new password.
 * - uid: The unique identifier of the user.
 * - token: The token provided for password reset validation.
 *
 * This mutation is used to finalize and confirm the password reset process by providing the new password and necessary credentials.
 */
export const PASSWORD_RESET_CONFIRM = gql`
	mutation passwordResetConfirm(
		$newPassword: String!
		$confirmPassword: String!
		$uid: String!
		$token: String!
	) {
		passwordResetConfirm(
			newPassword: $newPassword
			confirmPassword: $confirmPassword
			uid: $uid
			token: $token
		)
	}
`

export const SET_USER_CONTRACT_FILTER = gql`
	${fragmentUser}
	mutation setUserContractsFilter($contractList: [ID!]!) {
		setUserContractsFilter(input: { contractList: $contractList }) {
			user {
				...User
			}
		}
	}
`

export const SEND_RELEASE_PACKET_EMAIL = gql`
	mutation sendReleasePacketEmail(
		$releaseId: ID!
		$comparedReleaseId: ID
		$includeSvdPdfFiles: Boolean
		$includeSvdXmlFiles: Boolean
		$includeInspectionDocumentXmlFiles: Boolean
		$includeInspectionDocumentCsvFiles: Boolean
		$includeAkaPdf: Boolean
		$includeBaseFile: Boolean
		$includeHelpFiles: Boolean
		$includePcxFiles: Boolean
		$includeRevisionHistoryFile: Boolean
		$attachmentOption: Int
		$selectedContracts: [String]
		$isFiltered: Boolean
	) {
		sendReleasePacketEmail(
			input: {
				releaseId: $releaseId
				comparedReleaseId: $comparedReleaseId
				includeSvdPdfFiles: $includeSvdPdfFiles
				includeSvdXmlFiles: $includeSvdXmlFiles
				includeInspectionDocumentXmlFiles: $includeInspectionDocumentXmlFiles
				includeInspectionDocumentCsvFiles: $includeInspectionDocumentCsvFiles
				includeAkaPdf: $includeAkaPdf
				includeBaseFile: $includeBaseFile
				includeHelpFiles: $includeHelpFiles
				includePcxFiles: $includePcxFiles
				includeRevisionHistoryFile: $includeRevisionHistoryFile
				attachmentOption: $attachmentOption
				selectedContracts: $selectedContracts
				isFiltered: $isFiltered
			}
		) {
			inProgress
		}
	}
`

/**
 * GraphQL mutation to select contract region groups for a specific report.
 *
 * This mutation allows specifying which region groups should be associated with a given report.
 *
 * @param {ID} reportId - The unique identifier for the report.
 * @param {ID[]} selectedGroupIds - An array of unique identifiers for the region groups to be selected.
 */
export const SELECT_CONTRACT_REGION_GROUPS = gql`
	mutation selectContractRegionGroups(
		$reportId: ID!
		$selectedGroupIds: [ID]!
	) {
		selectContractRegionGroups(
			reportId: $reportId
			selectedGroupIds: $selectedGroupIds
		)
	}
`

export const SUBMIT_JUSTIFY_UNSATISFIED_REQUIREMENTS = gql`
	mutation submitJustifyUnsatisfiedRequirements(
		$items: [JustificationInputType!]!
		$reportId: ID!
	) {
		justifyUnsatisfiedRequirements(items: $items, reportId: $reportId) {
			justificationsCount
		}
	}
`

export const SELECT_REQUIREMENTS = gql`
	mutation selectRequirements($reportId: ID!, $requirementIds: [ID!]!) {
		selectRequirements(reportId: $reportId, requirementIds: $requirementIds) {
			selections {
				requirementId
				caliberId
			}
			error
		}
	}
`

export const SELECT_CONTRACT_REGION_GROUP_REQUIREMENTS = gql`
	mutation selectContractRegionGroupRequirements(
		$reportId: ID!
		$selection: [ContractRegionGroupsPerRequirement!]!
	) {
		selectContractRegionGroupRequirements(
			reportId: $reportId
			selection: $selection
		) {
			unchangedSelections
			deletedSelections
			createdSelections
		}
	}
`

export const UPDATE_SOFTWARE_SYSTEM = gql`
	mutation updateSoftwareSystem(
		$systemId: ID!
		$slogan: String!
		$website: String!
	) {
		updateSoftwareSystem(
			systemId: $systemId
			slogan: $slogan
			website: $website
		) {
			softwareSystem {
				id
				slogan
				website
				manufacturerName
			}
		}
	}
`

export const UPDATE_REPORT_SOFTWARE_SYSTEM_INFO = gql`
	mutation updateReportSoftwareSystemInfo(
		$reportInput: ReportInputType!
		$reportSystemInput: ReportSystemInputType!
		$mainContactInput: ContactPersonInputType!
		$additionalContactInput: ContactPersonInputType
	) {
		updateReportSoftwareSystemInfo(
			reportSystemInput: $reportSystemInput
			mainContactInput: $mainContactInput
			reportInput: $reportInput
			additionalContactInput: $additionalContactInput
		) {
			success
		}
	}
`

export const UPDATE_SOFTWARE_SYSTEM_COSTS = gql`
	mutation updateSoftwareSystemCosts(
		$systemId: ID!
		$tenantId: ID!
		$systemCosts: [SystemCostType!]!
	) {
		updateSoftwareSystemCosts(
			systemId: $systemId
			tenantId: $tenantId
			systemCosts: $systemCosts
		) {
			success
		}
	}
`

export const UPLOAD_CONTENT_CHECK_PROOF = gql`
	mutation contentCheckProofUpload(
		$systemId: ID!
		$contentCheckId: ID!
		$proofFiles: [Upload!]!
	) {
		contentCheckProofUpload(
			systemId: $systemId
			contentCheckId: $contentCheckId
			proofFiles: $proofFiles
		) {
			success
		}
	}
`

export const SEND_USER_MESSAGE_FOR_APPROVAL = gql`
	mutation sendUserMessageForApproval(
		$contentCheckId: ID
		$systemReportId: ID
		$softwareSystemId: ID!
		$userMessage: String!
		$files: [Upload]
	) {
		sendUserMessageForApproval(
			contentCheckId: $contentCheckId
			systemReportId: $systemReportId
			softwareSystemId: $softwareSystemId
			userMessage: $userMessage
			files: $files
		) {
			success
		}
	}
`

export const ACCEPT_PRIVACY_POLICY = gql`
	mutation acceptTermsOfUse($termsOfUseId: ID!) {
		acceptTermsOfUse(termsOfUseId: $termsOfUseId) {
			success
		}
	}
`

/**
 * GraphQL mutation for accepting all pending license contracts
 *
 * This mutation creates LicenseContractSignature records for all pending
 * license contracts for the current authenticated user. The backend determines
 * which contracts are pending and creates signatures accordingly.
 *
 * After successful acceptance, the user will no longer be redirected to the
 * license agreements page and can access the portal normally.
 *
 * Response structure:
 * {
 *   acceptAllLicenseAgreements: {
 *     success: boolean
 *     message: string  // Success or error message in German
 *     signaturesCreated: number  // Number of signatures created
 *   }
 * }
 *
 * Error handling:
 * - If success = false, the message field will contain a user-friendly error description
 * - The UI should display the error message and allow the user to retry
 *
 * @returns {Object} acceptAllLicenseAgreements - Acceptance result with success status
 */
export const ACCEPT_ALL_LICENSE_AGREEMENTS = gql`
	mutation AcceptAllLicenseAgreements {
		acceptAllLicenseAgreements {
			success
			message
			signaturesCreated
		}
	}
`
